import React, { useContext, useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@material-ui/core";
import MapboxGl from "mapbox-gl";
import BboxPolygon from "@turf/bbox-polygon";
import EquipGenSymbol from "../equip-design/equip-gen-symbol";
import { LangContext } from "../../context/lang";
import { paperList, toPixels, pixelToMm } from "../../constant/commons";
import { genLayersForGrayScale, _grayScale } from "./gen-load-pdf";

const Preview = (props: {
  map: MapboxGl.Map | undefined;
  open: boolean;
  onClose: Function;
  printparam: any;
  designEquips: any;
  anno: any;
  scaleBar: any;
  desTable: any;
  setloading: Function;
  genPdf: Function;
}) => {
  const {
    open,
    onClose,
    printparam,
    map,
    designEquips,
    anno,
    scaleBar,
    desTable,
    setloading,
    genPdf,
  } = props;
  const { lang } = useContext(LangContext);
  const [srcFrame, setsrcFrame] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (open && map) {
      const dpi = 32;
      const paperSize: any = paperList.find(
        (paper) => paper.id === printparam.paperSize
      );
      const paperWidth: any = paperSize[printparam.layout].width;
      const paperHeigth: any = paperSize[printparam.layout].height;
      const actualPixelRatio = window.devicePixelRatio;
      Object.defineProperty(window, "devicePixelRatio", {
        get: () => dpi / 96,
      });
      let hidden = document.createElement("div");
      hidden.id = "hidden-map";
      hidden.className = "hidden-map";
      document.body.appendChild(hidden);
      const container = document.createElement("div");
      container.id = "print-map";
      container.style.width = toPixels(paperWidth);
      container.style.height = toPixels(paperHeigth);

      hidden.appendChild(container);

      let preCleanStyle: any = map.getStyle();
      const cleanLayers = preCleanStyle.layers.filter((layer: any) => {
        if (
          String(layer.id).includes("equip") ||
          String(layer.id).includes("anno")
        ) {
          delete preCleanStyle.sources[layer.source];
          return false;
        } else {
          return true;
        }
      });

      const cleanStyle = {
        ...preCleanStyle,
        layers: printparam.grayscale
          ? genLayersForGrayScale(cleanLayers).filter((layer: any) => layer)
          : cleanLayers,
      };

      const renderMap = new MapboxGl.Map({
        container: container,
        center: map.getCenter(),
        zoom: printparam.scale,
        style: cleanStyle,
        interactive: false,
        preserveDrawingBuffer: true,
        fadeDuration: 0,
        attributionControl: false,
      });

      designEquips.map(async (eq: any) => {
        const equipSources = eq.source.data;
        let img = new Image();
        img.width = 100;
        img.height =
          equipSources.properties.equip_type === "equip-cable" &&
          equipSources.properties.cableType !== "aerial"
            ? 20
            : equipSources.properties.equip_type === "equip-riser-transformer"
            ? 60
            : 100;
        img.src = `data:image/svg+xml,${encodeURIComponent(
          ReactDOMServer.renderToString(
            <EquipGenSymbol property={equipSources.properties} />
          )
        )}`;

        img.onload = (event: any) => {
          renderMap.addImage(
            eq.type === "line"
              ? eq.paint["line-pattern"]
              : eq.layout["icon-image"],
            img
          );
        };
      });

      renderMap.once("load", async (e: MapboxGl.MapEventType["load"] | any) => {
        const renderMapEvent = e.target;
        renderMapEvent.setZoom(printparam.scale);
        designEquips.map(async (eq: any) => {
          if (eq.type !== "line") {
            await renderMapEvent.addLayer({
              ...eq,
              layout: { ...eq.layout, "icon-size": 0.3 },
            });
          } else {
            await renderMapEvent.addLayer(eq);
          }
        });

        if (anno?.length > 0) {
          anno.map((anNo: any) => {
            if (anNo.source.data) {
              e.target.addLayer(anNo);
            }
            return null;
          });
        }
      });

      renderMap.once(
        "idle",
        async (e: { type: "idle"; target: MapboxGl.Map }) => {
          const pdf: any = new jsPDF(
            printparam.layout,
            "mm",
            printparam.paperSize
          );
          const mapImage = printparam.grayscale
            ? await _grayScale("print-map")
            : e.target.getCanvas().toDataURL("image/png");

          pdf.addImage(
            mapImage,
            "PNG",
            paperSize[printparam.layout].originX,
            paperSize[printparam.layout].originY,
            paperWidth,
            paperHeigth
          );

          pdf.rect(
            paperSize[printparam.layout].originX,
            paperSize[printparam.layout].originY,
            paperWidth,
            paperHeigth,
            "S"
          );
          console.log(scaleBar);
          const scaleBarCanvas: HTMLCanvasElement = await html2canvas(
            scaleBar.current,
            { backgroundColor: null }
          );

          pdf.addImage(
            scaleBarCanvas.toDataURL("image/png"),
            "PNG",
            paperSize[printparam.layout].scaleBarX,
            paperSize[printparam.layout].scaleBarY,
            pixelToMm(scaleBar?.current.offsetWidth, dpi),
            pixelToMm(scaleBar?.current.offsetHeight, dpi)
          );

          if (printparam.compass) {
            let northArrowImage: HTMLImageElement = document.createElement(
              "img"
            );
            northArrowImage.src = require("../../material/images/north-arrow.png");
            pdf.addImage(
              northArrowImage,
              "PNG",
              paperSize[printparam.layout].northArrowX,
              paperSize[printparam.layout].northArrowY,
              8,
              11.56
            );
          }

          const descriptTable: HTMLCanvasElement = await html2canvas(
            desTable.current
          );
          pdf.addImage(
            descriptTable.toDataURL("image/jpeg"),
            "JPEG",
            paperSize[printparam.layout].desTableX,
            paperSize[printparam.layout].desTableY,
            pixelToMm(desTable?.current.offsetWidth, dpi),
            pixelToMm(desTable?.current.offsetHeight, dpi)
          );

          if (printparam.overview) {
            const containerOverview = document.createElement("div");
            containerOverview.id = "overview-map";
            containerOverview.style.width = toPixels(
              paperSize[printparam.layout].overviewWidth
            );
            containerOverview.style.height = toPixels(
              paperSize[printparam.layout].overviewHeight
            );
            hidden.appendChild(containerOverview);
            const bboxArea = e.target.getBounds().toArray();
            const arrayBbox: [number, number, number, number] | any = [
              ...bboxArea[0],
              ...bboxArea[1],
            ];

            const renderOverview = new MapboxGl.Map({
              container: containerOverview,
              bounds: [
                bboxArea[0][0] - 0.0011,
                bboxArea[0][1] - 0.0011,
                bboxArea[1][0] + 0.0011,
                bboxArea[1][1] + 0.0011,
              ],
              style: cleanStyle,
              interactive: false,
              preserveDrawingBuffer: true,
              fadeDuration: 0,
              attributionControl: false,
            });
            renderOverview.once(
              "load",
              async (e: MapboxGl.MapEventType["load"]) => {
                const renderOverviewEvent = e.target;

                const bbox_Polygon = BboxPolygon(arrayBbox);
                await renderOverviewEvent.addLayer({
                  id: "area",
                  type: "fill",
                  paint: { "fill-color": "#ff0000", "fill-opacity": 0.5 },
                  source: { type: "geojson", data: bbox_Polygon },
                });
              }
            );
            renderOverview.once(
              "idle",
              async (e: { type: "idle"; target: MapboxGl.Map }) => {
                const overviewImage = printparam.grayscale
                  ? await _grayScale("overview-map")
                  : e.target.getCanvas().toDataURL("image/png");

                pdf.addImage(
                  overviewImage,
                  "PNG",
                  paperSize[printparam.layout].overviewX,
                  paperSize[printparam.layout].overviewY,
                  paperSize[printparam.layout].overviewWidth,
                  paperSize[printparam.layout].overviewHeight
                );
                pdf.rect(
                  paperSize[printparam.layout].overviewX,
                  paperSize[printparam.layout].overviewY,
                  paperSize[printparam.layout].overviewWidth,
                  paperSize[printparam.layout].overviewHeight,
                  "S"
                );
                renderMap.remove();
                e.target.remove();
                const hiddenMap: any = document.getElementById("hidden-map");
                hiddenMap.parentNode.removeChild(hiddenMap);
                Object.defineProperty(window, "devicePixelRatio", {
                  get: function () {
                    return actualPixelRatio;
                  },
                });
                const pdfDataUrl: string = pdf.output("dataurlstring");
                // console.log(pdfDataUrl);
                setsrcFrame(pdfDataUrl);
              }
            );
          } else {
            renderMap.remove();
            const hiddenMap: any = document.getElementById("hidden-map");
            hiddenMap.parentNode.removeChild(hiddenMap);
            Object.defineProperty(window, "devicePixelRatio", {
              get: function () {
                return actualPixelRatio;
              },
            });
            const pdfDataUrl: string = pdf.output("dataurlstring");
            // console.log(pdfDataUrl);
            setsrcFrame(pdfDataUrl);
          }
        }
      );
    }
    return () => {
      if (open && map) {
      }
    };
  }, [open]);
  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={() => {}}
      keepMounted={false}
    >
      <DialogTitle>{lang.data.preview}</DialogTitle>
      <DialogContent>
        {srcFrame ? (
          <embed
            type="application/pdf"
            style={{ width: "100%", minHeight: 600 }}
            src={srcFrame}
          />
        ) : (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setloading(false);
            setsrcFrame(undefined);
            onClose();
          }}
          disabled={!Boolean(srcFrame)}
        >
          {lang.data.cancel}
        </Button>
        <Button
          disabled={!Boolean(srcFrame)}
          onClick={() => {
            setloading(false);
            onClose();
            setsrcFrame(undefined);
            genPdf();
          }}
          color="primary"
        >
          {lang.data.print}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Preview;
